import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, Grid, Divider, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Modal component to display top seller product details
 */
const TopSellerDetails = ({ open, onClose, data, fullScreen = false }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!data) {
    return null;
  }
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Top Seller: {data.product_name}
        </Typography>
        <Button 
          onClick={onClose} 
          color="inherit" 
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Product Details</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1">
                  <strong>Category:</strong> {data.category || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>MRP:</strong> ₹ {data.mrp?.toLocaleString('en-IN') || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Cost Price:</strong> ₹ {data.cost_price?.toLocaleString('en-IN') || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Profit Margin:</strong> {data.mrp && data.cost_price ? 
                    `${Math.round((data.mrp - data.cost_price) / data.mrp * 100)}%` : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Sales Summary</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1">
                  <strong>Total Stock:</strong> {data.total_stock} units
                </Typography>
                <Typography variant="body1">
                  <strong>Total Sales:</strong> {data.total_sales} units
                </Typography>
                <Typography variant="body1">
                  <strong>Stock Turnover Rate:</strong> {data.total_stock && data.total_sales ? 
                    `${(data.total_sales / data.total_stock).toFixed(2)}x` : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Sales by Marketplace */}
        <Typography variant="h6" sx={{ mb: 2 }}>Sales by Marketplace</Typography>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Marketplace</TableCell>
                <TableCell align="right">Units Sold</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sales_by_marketplace?.map((sale, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    {sale.marketplace}
                  </TableCell>
                  <TableCell align="right">{sale.units_sold}</TableCell>
                  <TableCell align="right">₹ {sale.amount?.toLocaleString('en-IN')}</TableCell>
                </TableRow>
              ))}
              {data.sales_by_marketplace?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No marketplace data available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Variants */}
        <Typography variant="h6" sx={{ mb: 2 }}>Product Variants</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.variants?.map((variant) => (
                <TableRow key={variant.id}>
                  <TableCell component="th" scope="row">{variant.sku}</TableCell>
                  <TableCell>{variant.color || 'N/A'}</TableCell>
                  <TableCell>{variant.size || 'N/A'}</TableCell>
                  <TableCell align="right">{variant.stock}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={variant.stock > 0 ? "In Stock" : "Out of Stock"} 
                      color={variant.stock > 0 ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              ))}
              {data.variants?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No variants available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopSellerDetails;
